import { BASE_URL } from "@/shared/config/constants"
import { i18n } from "@/shared/i18n"
import { hashSHA256 } from "@/shared/lib/utils/hash"
import { WebApp } from "@/shared/telegram"

const ERROR_HASH_KEY = "last_error_hash"

const getStoredErrorHash = (): Promise<string | null> =>
  new Promise(resolve => {
    WebApp.CloudStorage.getItem(ERROR_HASH_KEY, (error, value) => {
      if (error) {
        console.warn("[ErrorBoundary] CloudStorage read failed:", error)
      }
      resolve(error ? null : value || null)
    })
  })

const setStoredErrorHash = (hash: string): Promise<void> =>
  new Promise(resolve => {
    WebApp.CloudStorage.setItem(ERROR_HASH_KEY, hash, error => {
      if (error) {
        console.warn("[ErrorBoundary] CloudStorage write failed:", error)
      }
      resolve()
    })
  })

const getChatIdFromStartParam = (): number | null => {
  const startParam = WebApp.initDataUnsafe.start_param
  if (!startParam) return null

  for (const segment of startParam.split("==")) {
    let chatId: number | null = null

    if (segment.startsWith("-100")) {
      chatId = Number(segment)
    } else if (segment.startsWith("chat_id=-100")) {
      chatId = Number(segment.slice(8))
    }

    if (chatId !== null && !Number.isNaN(chatId) && Number.isFinite(chatId)) {
      return chatId
    }
  }
  return null
}

export const reportErrorToServer = async (error: Error, errorInfo: React.ErrorInfo): Promise<void> => {
  if (!import.meta.env.PROD) return

  try {
    const errorHash = await hashSHA256(`${error.message}${error.stack ?? ""}`)
    const storedHash = await getStoredErrorHash()

    if (storedHash === errorHash) {
      return
    }

    const response = await fetch(`${BASE_URL}/api/v1/frontend-errors`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "X-Telegram-Init-Data": WebApp.initData,
        Authorization: `Bearer ${WebApp.initData}`
      },
      body: JSON.stringify({
        error_message: error.message,
        stack_trace: error.stack ?? "",
        component_stack: errorInfo.componentStack ?? undefined,
        route: window.location.pathname,
        user_agent: navigator.userAgent,
        timestamp: new Date().toISOString(),
        locale: i18n.language,
        chat_id: getChatIdFromStartParam(),
        telegram: {
          version: WebApp.version,
          platform: WebApp.platform,
          color_scheme: WebApp.colorScheme,
          is_expanded: WebApp.isExpanded
        },
        polyfills: {
          needs_popover_polyfill: !("popover" in HTMLElement.prototype),
          needs_anchor_positioning_polyfill: !("anchorName" in document.documentElement.style)
        }
      })
    })

    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`)
    }

    await setStoredErrorHash(errorHash)
  } catch (reportingError) {
    console.error("[ErrorBoundary] Error during error reporting:", reportingError)
  }
}
